import React, { useEffect, useState } from "react";
import "./revised-styles.scss";
import vikramshilaLogo from "./assets/vikramshila-logo.png";
import aakashgangaLogo from "./assets/aakashganga-logo.png";
import inextLogo from "./assets/inextresearcher-logo.png";

const Arrow = () => <span aria-hidden="true">↗</span>;

const principles = [
  {
    number: "01",
    title: "Credibility",
    text: "Transparent publishing pathways grounded in academic integrity, responsible research and clear editorial processes.",
  },
  {
    number: "02",
    title: "Inclusion",
    text: "Accessible and affordable opportunities for people at different stages of their research journey.",
  },
  {
    number: "03",
    title: "Openness",
    text: "Open access, transparent processes and wider discovery help knowledge travel beyond boundaries.",
  },
  {
    number: "04",
    title: "Technology",
    text: "Purposeful automation reduces friction while preserving meaningful human oversight.",
  },
];

const AAKASHGANGA_URL = "https://www.aakashgangaopen.in/";
const INEXT_URL = "https://inextresearcher.com/";

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Vikramshila Research | Research, Learning & Publishing";
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Close mobile menu on Escape key or when window resized to desktop
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    const handleResize = () => {
      if (window.innerWidth > 992) setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Prevent background scrolling when mobile navigation is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const handleNavScroll = (e, targetId) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (targetId === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="vikramshila-revised-home">
      <main>
        {/* Header */}
        <header className="site-header">
          <div className="header-inner">
            <a
              className="brand"
              href="#top"
              onClick={(e) => handleNavScroll(e, "#top")}
              aria-label="Vikramshila Research home"
            >
              <img src={vikramshilaLogo} alt="Vikramshila" />
              <span>Vikramshila Research</span>
            </a>

            {/* Desktop Navigation */}
            <nav className="desktop-nav" aria-label="Primary navigation">
              <a href="#platforms" onClick={(e) => handleNavScroll(e, "#platforms")}>
                Platforms
              </a>
              <a href="#about" onClick={(e) => handleNavScroll(e, "#about")}>
                About
              </a>
              <a href="#principles" onClick={(e) => handleNavScroll(e, "#principles")}>
                Principles
              </a>
              <a href="#leadership" onClick={(e) => handleNavScroll(e, "#leadership")}>
                Leadership
              </a>
              <a href="#contact" onClick={(e) => handleNavScroll(e, "#contact")}>
                Contact
              </a>
            </nav>

            <div className="header-actions">
              <a
                className="header-cta desktop-cta"
                href="#platforms"
                onClick={(e) => handleNavScroll(e, "#platforms")}
              >
                Find your platform <span aria-hidden="true">↓</span>
              </a>

              {/* Hamburger Button for Mobile / Tablet */}
              <button
                type="button"
                className={`mobile-menu-toggle ${mobileMenuOpen ? "is-active" : ""}`}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Drawer */}
          <div
            className={`mobile-nav-drawer ${mobileMenuOpen ? "is-open" : ""}`}
            aria-hidden={!mobileMenuOpen}
          >
            <nav className="mobile-nav" aria-label="Mobile navigation">
              <a href="#platforms" onClick={(e) => handleNavScroll(e, "#platforms")}>
                Platforms
              </a>
              <a href="#about" onClick={(e) => handleNavScroll(e, "#about")}>
                About
              </a>
              <a href="#principles" onClick={(e) => handleNavScroll(e, "#principles")}>
                Principles
              </a>
              <a href="#leadership" onClick={(e) => handleNavScroll(e, "#leadership")}>
                Leadership
              </a>
              <a href="#contact" onClick={(e) => handleNavScroll(e, "#contact")}>
                Contact
              </a>
              <a
                className="mobile-nav-cta"
                href="#platforms"
                onClick={(e) => handleNavScroll(e, "#platforms")}
              >
                Find your platform <span aria-hidden="true">↓</span>
              </a>
            </nav>
          </div>
        </header>

        {/* Mobile Navigation Overlay */}
        {mobileMenuOpen && (
          <div
            className="mobile-nav-overlay"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Hero Section */}
        <section className="hero" id="top">
          <div className="hero-copy">
            <p className="eyebrow">Research · Learning · Publishing</p>
            <h1>
              Building credible pathways from <em>ideas to impact.</em>
            </h1>
            <p className="hero-intro">
              Vikramshila Research is the parent organisation behind Aakashganga Open
              and iNextResearcher—two platforms serving different stages of the
              research journey.
            </p>
            <div className="hero-actions">
              <a
                className="button button-primary"
                href="#platforms"
                onClick={(e) => handleNavScroll(e, "#platforms")}
              >
                Explore our platforms <span aria-hidden="true">↓</span>
              </a>
              <a
                className="text-link"
                href="#about"
                onClick={(e) => handleNavScroll(e, "#about")}
              >
                Why Vikramshila <span aria-hidden="true">→</span>
              </a>
            </div>
            <div className="hero-proof">
              <span className="proof-line" aria-hidden="true" />
              <p>Founded in India · Designed for research communities everywhere</p>
            </div>
          </div>

          <aside className="pathway-card" aria-label="Choose a Vikramshila platform">
            <div className="pathway-heading">
              <span>Two platforms</span>
              <strong>One mission</strong>
            </div>
            <a
              className="pathway pathway-aakash"
              href={AAKASHGANGA_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="pathway-audience">Researchers · Journals · Institutions</span>
              <img src={aakashgangaLogo} alt="Aakashganga Open" />
              <span className="pathway-action">
                Scholarly publishing <Arrow />
              </span>
            </a>
            <a
              className="pathway pathway-inext"
              href={INEXT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="pathway-audience">Students · Schools · Mentors</span>
              <img src={inextLogo} alt="iNextResearcher" />
              <span className="pathway-action">
                Student research publishing <Arrow />
              </span>
            </a>
          </aside>
        </section>

        {/* Purpose Strip */}
        <section className="purpose-strip" aria-label="Vikramshila purpose">
          <p>Credible.</p>
          <span aria-hidden="true">✦</span>
          <p>Inclusive.</p>
          <span aria-hidden="true">✦</span>
          <p>Open.</p>
        </section>

        {/* Platforms Section */}
        <section className="section platforms" id="platforms">
          <div className="section-heading">
            <p className="eyebrow">Choose the right platform</p>
            <h2>Two platforms. Different research journeys.</h2>
            <p>
              Select the platform designed for your stage of research. Service details,
              registration and publishing journeys continue on the respective websites.
            </p>
          </div>

          <div className="platform-grid">
            <article className="platform-card aakash-card">
              <div className="platform-topline">
                <span>01</span>
                <span>For researchers, journals &amp; institutions</span>
              </div>
              <div className="logo-wrap aakash-logo">
                <img src={aakashgangaLogo} alt="Aakashganga Open" />
              </div>
              <h3>Publish and disseminate scholarly research.</h3>
              <p>
                Open-access publishing and journal infrastructure for researchers,
                universities, institutions, academic societies and conference organisers.
              </p>
              <ul>
                <li>Discipline-specific Mega Journals</li>
                <li>Journal hosting for institutions and academic societies</li>
                <li>Conference-paper publishing and scholarly dissemination</li>
              </ul>
              <a
                className="platform-link"
                href={AAKASHGANGA_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit Aakashganga Open <Arrow />
              </a>
            </article>

            <article className="platform-card inext-card">
              <div className="platform-topline">
                <span>02</span>
                <span>For students and those who guide them</span>
              </div>
              <div className="logo-wrap inext-logo">
                <img src={inextLogo} alt="iNextResearcher" />
              </div>
              <h3>Turn curiosity into credible contribution.</h3>
              <p>
                A structured research and publishing pathway for school and
                undergraduate students, supported by educators, schools, counsellors
                and parents.
              </p>
              <ul>
                <li>Guidance from the first idea to publication</li>
                <li>Peer review, DOI and open access</li>
                <li>Opportunities for students, educators and partner organisations</li>
              </ul>
              <a
                className="platform-link"
                href={INEXT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit iNextResearcher <Arrow />
              </a>
            </article>
          </div>
        </section>

        {/* About Section */}
        <section className="section about" id="about">
          <div className="about-marker" aria-label="Vikramshila Research">
            <svg
              className="about-wordmark"
              viewBox="0 0 620 160"
              role="img"
              aria-label="Vikramshila Research"
            >
              <text x="50" y="74" textLength="520" lengthAdjust="spacing">
                VIKRAMSHILA
              </text>
              <circle cx="101" cy="19" r="5" />
              <circle cx="464" cy="19" r="5" />
              <text
                className="research-line"
                x="50"
                y="150"
                textLength="520"
                lengthAdjust="spacing"
              >
                RESEARCH
              </text>
            </svg>
            <p>Founded in India, Global in Outlook</p>
          </div>
          <div className="about-copy">
            <p className="eyebrow">Why Vikramshila</p>
            <h2>We build the infrastructure that helps credible research travel further.</h2>
            <div className="about-columns">
              <p>
                Vikramshila Research builds accessible, technology-enabled pathways
                for learning about research, publishing original work and discovering
                new knowledge.
              </p>
              <p>
                Our platforms connect students and educators with researchers,
                institutions and academic societies through publishing environments
                designed around transparency, accessibility and human oversight.
              </p>
            </div>
          </div>
        </section>

        {/* Principles Section */}
        <section className="section principles" id="principles">
          <div className="section-heading compact">
            <p className="eyebrow">What guides us</p>
            <h2>Purpose, expressed through practice.</h2>
          </div>
          <div className="principle-grid">
            {principles.map((principle) => (
              <article key={principle.number}>
                <span>{principle.number}</span>
                <h3>{principle.title}</h3>
                <p>{principle.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Leadership Section */}
        <section className="section leadership" id="leadership">
          <div>
            <p className="eyebrow">Leadership</p>
            <h2>Grounded in publishing, research and education.</h2>
          </div>
          <div className="leader-list">
            <article>
              <span>Founder &amp; CEO</span>
              <h3>Dr Vivek Mehra</h3>
              <p>
                Former publishing CEO, institutional builder and advocate for more
                equitable research dissemination.
              </p>
            </article>
            <article>
              <span>Co-founder &amp; COO</span>
              <h3>Shafina Segon</h3>
              <p>
                Publishing leader and education strategist focused on research
                integrity and credible student scholarship.
              </p>
            </article>
          </div>
        </section>

        {/* Contact Section */}
        <section className="contact" id="contact">
          <div className="contact-heading">
            <p className="eyebrow">Choose your next step</p>
            <h2>Publish your work—or build something with us.</h2>
          </div>
          <div className="route-grid">
            <a
              className="route-card"
              href={AAKASHGANGA_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>For researchers &amp; institutions</span>
              <strong>Publish through Aakashganga Open</strong>
              <Arrow />
            </a>
            <a
              className="route-card"
              href={INEXT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>For students &amp; their guides</span>
              <strong>Publish through iNextResearcher</strong>
              <Arrow />
            </a>
            <a className="route-card" href="mailto:contactus@vikramshilaedu.in">
              <span>Corporate &amp; partnership enquiries</span>
              <strong>Partner with Vikramshila</strong>
              <Arrow />
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer>
          <div className="footer-brand">
            <img src={vikramshilaLogo} alt="Vikramshila" />
            <p>The parent organisation behind Aakashganga Open and iNextResearcher.</p>
          </div>
          <div>
            <strong>Platforms</strong>
            <a href={AAKASHGANGA_URL} target="_blank" rel="noopener noreferrer">
              Aakashganga Open
            </a>
            <a href={INEXT_URL} target="_blank" rel="noopener noreferrer">
              iNextResearcher
            </a>
          </div>
          <div>
            <strong>Publisher</strong>
            <p>Vikramshila Research Pvt Ltd</p>
            <p>
              I-1667, 3rd Floor, C R Park,
              <br />
              New Delhi 110019, India
            </p>
            <a href="mailto:contactus@vikramshilaedu.in">contactus@vikramshilaedu.in</a>
          </div>
          <div className="footer-bottom">
            <span>© 2026 Vikramshila Research Pvt Ltd</span>
            <span>Credibility · Inclusion · Openness · Technology</span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default HomePage;